import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {isConfigPageAccessible} from "~/workspaceConfig/configPageAccess";
import {createFileRoute, notFound} from "@tanstack/react-router";
import {z as zod} from "zod";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {
    getWorkspaceConfigManifest,
    hasWorkspaceUiConfig,
} from "~/workspaceConfig/configRuntime";
import {ConfigScaffoldPage} from "./-page";

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_configScaffold/$pageKey",
)({
    validateSearch: zod.object({
        /** Billing checkout actions retain their query parameters on configured pages. */
        openPlanSelector: zod.string().optional(),
        canCloseCheckoutModal: zod.boolean().optional(),
        isPaymentFailed: zod.boolean().optional(),
        subscriptionPlanId: uuidZodValidator.optional(),
        paymentCurrency: uuidZodValidator.optional(),
        paymentFrequency: uuidZodValidator.optional(),
        eventId: zod.string().optional(),
        journeyId: zod.string().optional(),
        eventContainerId: zod.string().optional(),
        returnPageId: zod.string().uuid().optional(),
        eventContainerTemplateId: zod.string().uuid().optional(),
        statusId: zod.string().optional(),
        view: zod.string().optional(),
        openUserId: zod.string().optional(),
        category: zod.string().optional(),
        dispositionLevel: zod.coerce.number().min(1).max(4).optional(),
        notificationFilter: zod.string().optional(),
        /** Ottosync data-source detail deep-link. */
        dataSourceId: zod.string().uuid().optional(),
        connectionAttemptId: zod.string().uuid().optional(),
        /** Ottosync connect-onboarding deep-link. */
        onboardingJourneyId: zod.string().optional(),
        /** Collections detail deep-link (same page type, detail shell). */
        collectionId: zod.string().optional(),
        /** Report detail / edit deep-link — only param those pages need. */
        reportId: zod.string().optional(),
        /** Config page id to return to on back (collection detail, reports list, …). */
        parentPageId: zod.string().uuid().optional(),
        /** After report edit save/cancel — boolean (matches production report route). */
        saved: zod.boolean().optional(),
        /** Amaron log-call entry from disposition tracker. */
        from: zod.string().optional(),
        /** Leaderboard saved board. */
        boardId: zod.string().uuid().optional(),
        /** Leaderboard create mode on the same config page. */
        create: zod
            .union([zod.boolean(), zod.literal("true"), zod.literal("false")])
            .optional()
            .transform((value) => {
                if (value === true || value === "true") {
                    return true;
                }
                return undefined;
            }),
        "default-date-range": zod.string().optional(),
        /** Ottokart quotation detail deep-link — quotation ID for sub-pages. */
        quotationId: zod.string().uuid().optional(),
        /** Ottokart quotation sub-page pagination. */
        quotationOffset: zod.coerce.number().optional(),
        quotationLimit: zod.coerce.number().optional(),
        /** Ottokart product sub-page product ID. */
        productId: zod.string().uuid().optional(),
        /** Ottokart standalone variant editor. */
        variantId: zod.string().uuid().optional(),
        /** Ottokart quotation editor PDF preview (`draft` or a quotation id). */
        preview: zod.string().optional(),
        /** Bluestores invite-sent banner. */
        inviteSent: zod.boolean().optional(),
        inviteLink: zod.string().optional(),
        /** Store Configuration / Store Configuration Edit deep-link from Bluestores. */
        retailLocationId: zod.string().uuid().optional(),
        /** Open the StoreConfigurationEdit page in default-store mode. */
        defaultStore: zod
            .union([zod.boolean(), zod.literal("true"), zod.literal("false")])
            .optional(),
        /** Open the store / default-store page editor after landing. */
        editStore: zod
            .union([zod.boolean(), zod.literal("true"), zod.literal("false")])
            .optional(),
        /** Transfer form ↔ preview session. */
        transferSessionId: zod.string().optional(),
        /** Transfer log detail deep-link. */
        operationId: zod.string().uuid().optional(),
        /** Open a ticket detail from a ticket notification. */
        ticketId: zod.string().uuid().optional(),
    }),
    beforeLoad: ({context, params}) => {
        if (uuidZodValidator.safeParse(params.pageKey).success !== true) {
            throw notFound();
        }
        if (hasWorkspaceUiConfig(params.workspaceId as Uuid) !== true) {
            throw notFound();
        }
        const manifest = getWorkspaceConfigManifest(params.workspaceId as Uuid);
        const page = manifest.pages[params.pageKey];
        if (page === undefined || !isConfigPageAccessible(page, context.currentWorkspaceUser.templateId)) {
            throw notFound();
        }
    },
    component: RouteComponent,
});

function RouteComponent() {
    // Keep the page mounted across `/page` and `/page/folder/...`.
    // Those are sibling index and splat routes; rendering here avoids remounting
    // the header when the library path changes.
    return <ConfigScaffoldPage />;
}
